import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans } from 'react-i18next';
import { Loader2, Printer } from 'lucide-react';
import {
  getOrderFulfillmentMethod,
  isFulfillmentReadyOrderedOrder,
  isManualApprovalPending,
  mapTransitionError,
  useTranslation,
} from '@dukanti/shared';
import type { AdminOrder, CourierOrderAssignmentSummary, TranslationKey } from '@dukanti/shared';
import { useApproveManualOrder } from '@/hooks/use-approve-manual-order';
import { useDeclineManualOrder } from '@/hooks/use-decline-manual-order';
import { useCollectPickupOrder } from '@/hooks/use-collect-pickup-order';
import { useShipOrder } from '@/hooks/use-ship-order';
import { useDeliverOrder } from '@/hooks/use-deliver-order';
import { useRejectOrder } from '@/hooks/use-reject-order';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

export interface OrderDetailActionsProps {
  readonly order: AdminOrder;
  readonly storeId: string;
  readonly courierAssignment?: CourierOrderAssignmentSummary | null;
}

type ActiveModal = 'collect' | 'ship' | 'deliver' | 'reject' | 'approve' | 'decline' | null;

/**
 * F-022: Actions sidebar for the order detail page.
 *
 * Renders action buttons based on `order.status` and manages the
 * confirmation AlertDialogs via local state. Each mutation hook handles
 * its own toast on success/error.
 *
 * Modal bold interpolation uses `<Trans>` with the named-slot `<bold>`
 * convention established in `delete-product-dialog.tsx`.
 *
 * Per-status buttons:
 *   ordered   → Ship (primary) + Reject (destructive)
 *   shipped    → Deliver (primary)
 *   delivered | cancelled | rejected → no action buttons
 */
export function OrderDetailActions({
  order,
  storeId,
  courierAssignment,
}: OrderDetailActionsProps): React.JSX.Element {
  const { t, dir } = useTranslation();
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const collectTriggerRef = useRef<HTMLButtonElement>(null);

  const collectMutation = useCollectPickupOrder({ orderId: order.id, storeId });
  const isPickup = getOrderFulfillmentMethod(order) === 'pickup';
  const shipMutation = useShipOrder({ orderId: order.id, storeId });
  const deliverMutation = useDeliverOrder({ orderId: order.id, storeId });
  const rejectMutation = useRejectOrder({ orderId: order.id, storeId });
  const approveMutation = useApproveManualOrder({ orderId: order.id, storeId });
  const declineMutation = useDeclineManualOrder({ orderId: order.id, storeId });

  const isAnyPending =
    collectMutation.isPending ||
    shipMutation.isPending ||
    deliverMutation.isPending ||
    rejectMutation.isPending ||
    approveMutation.isPending ||
    declineMutation.isPending;

  function closeModal(): void {
    setActiveModal(null);
  }

  function handleModalOpenChange(open: boolean): void {
    if (!open) closeModal();
  }

  function handleShipAction(): void {
    shipMutation.mutate(undefined, { onSettled: closeModal });
  }

  function handleDeliverAction(): void {
    deliverMutation.mutate(undefined, { onSettled: closeModal });
  }

  function handleRejectAction(): void {
    rejectMutation.mutate(undefined, { onSettled: closeModal });
  }

  function handleApproveAction(): void {
    approveMutation.mutate(undefined, { onSettled: closeModal });
  }

  function handleDeclineAction(): void {
    declineMutation.mutate(undefined, { onSettled: closeModal });
  }

  const isApprovalPending = isManualApprovalPending(order);
  const showCollect = isPickup && isFulfillmentReadyOrderedOrder(order);
  const showShip = !isPickup && isFulfillmentReadyOrderedOrder(order);
  const hasCourierProofGate = !isPickup && !!courierAssignment && order.status === 'shipped';
  const showDeliver = !isPickup && order.status === 'shipped' && !hasCourierProofGate;
  const showReject = order.status === 'ordered' && !isApprovalPending;
  const isTerminal =
    order.status === 'delivered' || order.status === 'cancelled' || order.status === 'rejected';
  const terminalMessageKey =
    order.status === 'delivered'
      ? isPickup
        ? 'admin.orders.actions.collectedSuccess'
        : 'admin.orders.actions.successToasts.delivered'
      : order.status === 'rejected'
        ? 'admin.orders.actions.successToasts.rejected'
        : 'admin.orders.actions.successToasts.cancelled';

  function releaseDialogFocus(): void {
    if (typeof document === 'undefined') return;
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  return (
    <section
      data-testid="order-detail-actions"
      className="border-border bg-card shadow-admin-card flex flex-col gap-3 rounded-lg border p-6"
    >
      {!isPickup && !isApprovalPending ? (
        <Button asChild variant="outline" aria-label={t('admin.orders.shippingLabel.printAria')}>
          <Link to={`/orders/${order.id}/label`} data-testid="order-detail-print-label-link">
            <Printer aria-hidden="true" className="size-4" strokeWidth={1.75} />
            <span>{t('admin.orders.shippingLabel.open')}</span>
          </Link>
        </Button>
      ) : null}

      {isTerminal ? (
        <p className="text-muted-foreground text-center text-sm">{t(terminalMessageKey)}</p>
      ) : (
        <>
          {isApprovalPending ? (
            <>
              <div className="border-honey/40 bg-honey/10 text-foreground rounded-lg border p-3 text-start text-sm">
                {t(
                  isPickup
                    ? 'admin.orders.manualApproval.pendingPickupCallout'
                    : 'admin.orders.manualApproval.pendingCallout',
                )}
              </div>
              <Button
                type="button"
                variant="default"
                disabled={isAnyPending}
                onClick={() => setActiveModal('approve')}
                data-testid="action-approve-manual-order"
                aria-busy={approveMutation.isPending || undefined}
              >
                {approveMutation.isPending ? (
                  <>
                    <Loader2
                      aria-hidden="true"
                      className="size-4 animate-spin"
                      strokeWidth={1.75}
                    />
                    <span>{t('admin.orders.manualApproval.approving' as TranslationKey)}</span>
                  </>
                ) : (
                  <span>{t('admin.orders.manualApproval.approve' as TranslationKey)}</span>
                )}
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={isAnyPending}
                onClick={() => setActiveModal('decline')}
                data-testid="action-decline-manual-order"
                aria-busy={declineMutation.isPending || undefined}
              >
                {declineMutation.isPending ? (
                  <>
                    <Loader2
                      aria-hidden="true"
                      className="size-4 animate-spin"
                      strokeWidth={1.75}
                    />
                    <span>{t('admin.orders.manualApproval.declining' as TranslationKey)}</span>
                  </>
                ) : (
                  <span>{t('admin.orders.manualApproval.decline' as TranslationKey)}</span>
                )}
              </Button>
            </>
          ) : null}

          {showCollect ? (
            <Button
              type="button"
              disabled={isAnyPending}
              onClick={() => {
                collectMutation.reset();
                setActiveModal('collect');
              }}
              ref={collectTriggerRef}
              data-testid="action-collect"
              aria-busy={collectMutation.isPending}
            >
              {t(
                collectMutation.isPending
                  ? 'admin.orders.actions.collecting'
                  : 'admin.orders.actions.markCollected',
              )}
            </Button>
          ) : null}
          {showShip && (
            <Button
              type="button"
              variant="default"
              disabled={isAnyPending}
              onClick={() => setActiveModal('ship')}
              data-testid="action-ship"
              aria-busy={shipMutation.isPending || undefined}
            >
              {shipMutation.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" strokeWidth={1.75} />
                  <span>{t('admin.orders.actions.shipping')}</span>
                </>
              ) : (
                <span>{t('admin.orders.actions.ship')}</span>
              )}
            </Button>
          )}

          {showDeliver && (
            <Button
              type="button"
              variant="default"
              disabled={isAnyPending}
              onClick={() => setActiveModal('deliver')}
              data-testid="action-deliver"
              aria-busy={deliverMutation.isPending || undefined}
            >
              {deliverMutation.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" strokeWidth={1.75} />
                  <span>{t('admin.orders.actions.delivering')}</span>
                </>
              ) : (
                <span>{t('admin.orders.actions.deliver')}</span>
              )}
            </Button>
          )}

          {hasCourierProofGate ? (
            <div className="border-honey/40 bg-honey/10 text-foreground rounded-lg border p-3 text-start text-sm">
              {t('admin.orders.courierProof.awaitingProof' as TranslationKey)}
            </div>
          ) : null}

          {showReject && (
            <Button
              type="button"
              variant="destructive"
              disabled={isAnyPending}
              onClick={() => setActiveModal('reject')}
              data-testid="action-reject"
              aria-busy={rejectMutation.isPending || undefined}
            >
              {rejectMutation.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" strokeWidth={1.75} />
                  <span>{t('admin.orders.actions.rejecting')}</span>
                </>
              ) : (
                <span>{t('admin.orders.actions.reject')}</span>
              )}
            </Button>
          )}
        </>
      )}

      <AlertDialog
        open={activeModal === 'collect'}
        onOpenChange={(open) => {
          if (!open && !collectMutation.isPending) closeModal();
        }}
      >
        <AlertDialogContent
          dir={dir}
          data-testid="collect-pickup-order-dialog"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            collectTriggerRef.current?.focus();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.orders.actions.collectTitle')}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            {t('admin.orders.actions.collectDescription')}
          </AlertDialogDescription>
          {collectMutation.isError || (collectMutation.data && !collectMutation.data.success) ? (
            <p role="alert" className="text-destructive text-sm">
              {t(mapTransitionError(collectMutation.error, collectMutation.data, 'collect'))}
            </p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="ghost" disabled={collectMutation.isPending}>
                {t('common.cancel')}
              </Button>
            </AlertDialogCancel>
            <Button
              type="button"
              disabled={!showCollect || isAnyPending}
              data-testid="collect-pickup-order-confirm"
              onClick={() =>
                collectMutation.mutate(undefined, {
                  onSuccess: (result) => {
                    if (result.success) closeModal();
                  },
                })
              }
            >
              {t(
                collectMutation.isPending
                  ? 'admin.orders.actions.collecting'
                  : 'admin.orders.actions.markCollected',
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={activeModal === 'approve'} onOpenChange={handleModalOpenChange}>
        <AlertDialogContent dir={dir} data-testid="approve-manual-order-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('admin.orders.manualApproval.approveModalTitle' as TranslationKey)}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            {t('admin.orders.manualApproval.approvePrompt' as TranslationKey)}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={approveMutation.isPending}
              onClick={() => {
                releaseDialogFocus();
                closeModal();
              }}
            >
              {t('admin.orders.actions.modalCancel')}
            </Button>
            <Button
              type="button"
              variant="default"
              disabled={approveMutation.isPending}
              onClick={() => {
                releaseDialogFocus();
                handleApproveAction();
              }}
              data-testid="approve-manual-order-confirm"
            >
              {t('admin.orders.actions.modalConfirm')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={activeModal === 'decline'} onOpenChange={handleModalOpenChange}>
        <AlertDialogContent dir={dir} data-testid="decline-manual-order-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('admin.orders.manualApproval.declineModalTitle' as TranslationKey)}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            {t('admin.orders.manualApproval.declinePrompt' as TranslationKey)}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={declineMutation.isPending}
              onClick={() => {
                releaseDialogFocus();
                closeModal();
              }}
            >
              {t('admin.orders.actions.modalCancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={declineMutation.isPending}
              onClick={() => {
                releaseDialogFocus();
                handleDeclineAction();
              }}
              data-testid="decline-manual-order-confirm"
            >
              {t('admin.orders.actions.modalConfirm')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Ship order dialog */}
      <AlertDialog open={activeModal === 'ship'} onOpenChange={handleModalOpenChange}>
        <AlertDialogContent dir={dir} data-testid="ship-order-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.orders.actions.shipModalTitle')}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription asChild>
            <div>
              <Trans
                i18nKey="admin.orders.actions.shipPrompt"
                components={{ bold: <strong className="font-semibold" /> }}
              />
            </div>
          </AlertDialogDescription>
          <AlertDialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={shipMutation.isPending}
              onClick={() => {
                releaseDialogFocus();
                closeModal();
              }}
              data-testid="ship-order-cancel"
            >
              {t('admin.orders.actions.modalCancel')}
            </Button>
            <Button
              type="button"
              variant="default"
              disabled={shipMutation.isPending}
              onClick={() => {
                releaseDialogFocus();
                handleShipAction();
              }}
              data-testid="ship-order-confirm"
              aria-busy={shipMutation.isPending || undefined}
            >
              {shipMutation.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" strokeWidth={1.75} />
                  <span>{t('admin.orders.actions.shipping')}</span>
                </>
              ) : (
                <span>{t('admin.orders.actions.modalConfirm')}</span>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deliver order dialog */}
      <AlertDialog open={activeModal === 'deliver'} onOpenChange={handleModalOpenChange}>
        <AlertDialogContent dir={dir} data-testid="deliver-order-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.orders.actions.deliverModalTitle')}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>{t('admin.orders.actions.deliverPrompt')}</AlertDialogDescription>
          <AlertDialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={deliverMutation.isPending}
              onClick={() => {
                releaseDialogFocus();
                closeModal();
              }}
              data-testid="deliver-order-cancel"
            >
              {t('admin.orders.actions.modalCancel')}
            </Button>
            <Button
              type="button"
              variant="default"
              disabled={deliverMutation.isPending}
              onClick={() => {
                releaseDialogFocus();
                handleDeliverAction();
              }}
              data-testid="deliver-order-confirm"
              aria-busy={deliverMutation.isPending || undefined}
            >
              {deliverMutation.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" strokeWidth={1.75} />
                  <span>{t('admin.orders.actions.delivering')}</span>
                </>
              ) : (
                <span>{t('admin.orders.actions.modalConfirm')}</span>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject order dialog */}
      <AlertDialog open={activeModal === 'reject'} onOpenChange={handleModalOpenChange}>
        <AlertDialogContent dir={dir} data-testid="reject-order-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.orders.actions.rejectModalTitle')}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription asChild>
            <div>
              <Trans
                i18nKey="admin.orders.actions.rejectPrompt"
                components={{ bold: <strong className="font-semibold" /> }}
              />
            </div>
          </AlertDialogDescription>
          <AlertDialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={rejectMutation.isPending}
              onClick={() => {
                releaseDialogFocus();
                closeModal();
              }}
              data-testid="reject-order-cancel"
            >
              {t('admin.orders.actions.modalCancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={rejectMutation.isPending}
              onClick={() => {
                releaseDialogFocus();
                handleRejectAction();
              }}
              data-testid="reject-order-confirm"
              aria-busy={rejectMutation.isPending || undefined}
            >
              {rejectMutation.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" strokeWidth={1.75} />
                  <span>{t('admin.orders.actions.rejecting')}</span>
                </>
              ) : (
                <span>{t('admin.orders.actions.modalConfirm')}</span>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
